import { Alert } from '@mui/material';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { DASHBOARD_CODES, firstNavPath } from '@app/shared';
import { useAuth } from '../features/auth/AuthProvider';
import { LoginPage } from '../features/auth/LoginPage';
import { SignupPage } from '../features/auth/SignupPage';
import { ReleasedFeature } from '../release';
import { ResetPasswordPage } from '../features/auth/PasswordResetPages';
import { RequireAuth } from '../features/auth/guards';
import { billingRoutes } from '../features/billing/routes';
import { configurationRoutes } from '../features/configuration/routes';
import { customersRoutes } from '../features/customers/routes';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { enrollmentsRoutes } from '../features/enrollments/routes';
import { lifelineRoutes } from '../features/lifeline/routes';
import { healthRoutes } from '../features/health/routes';
import { inventoryRoutes } from '../features/inventory/routes';
import { messagingRoutes } from '../features/messaging/routes';
import { ordersRoutes } from '../features/orders/routes';
import { plansRoutes } from '../features/plans/routes';
import { PortalPage } from '../features/portal/PortalPage';
import { HqPage } from '../features/hq/HqPage';
import { portsRoutes } from '../features/ports/routes';
import { promotionsRoutes } from '../features/promotions/routes';
import { reportsRoutes } from '../features/reports/routes';
import { ticketsRoutes } from '../features/tickets/routes';
import { usersRoutes } from '../features/users/routes';
import { AppShell } from './AppShell';

/**
 * The home page: the dashboard for users who hold any of its widgets, otherwise the first
 * screen their permissions allow.
 */
function HomeRedirect() {
  const { can } = useAuth();
  if (DASHBOARD_CODES.some(can)) return <DashboardPage />;
  const path = firstNavPath(can);
  if (!path)
    return (
      <Alert severity="info">
        Your user group has no screens assigned yet. Ask an administrator to add permissions.
      </Alert>
    );
  return <Navigate to={path} replace />;
}

export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  {
    path: '/hq',
    element: (
      <ReleasedFeature name="hq-instance" page>
        <HqPage />
      </ReleasedFeature>
    ),
  },
  {
    path: '/signup',
    element: (
      <ReleasedFeature name="mvno-signup" page>
        <SignupPage />
      </ReleasedFeature>
    ),
  },
  {
    path: '/portal/:slug',
    element: (
      <ReleasedFeature name="customer-portal" page>
        <PortalPage />
      </ReleasedFeature>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <ReleasedFeature name="forgot-password" page>
        <ResetPasswordPage />
      </ReleasedFeature>
    ),
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <HomeRedirect /> },
      ...usersRoutes,
      ...plansRoutes,
      ...inventoryRoutes,
      ...healthRoutes,
      // Before the customer routes: `customers/payment-search` is not a customer id.
      ...billingRoutes,
      ...customersRoutes,
      ...enrollmentsRoutes,
      ...lifelineRoutes,
      ...ticketsRoutes,
      ...reportsRoutes,
      ...promotionsRoutes,
      ...ordersRoutes,
      ...portsRoutes,
      ...messagingRoutes,
      ...configurationRoutes,
      { path: '*', element: <Alert severity="warning">Page not found.</Alert> },
    ],
  },
];

export const createRouter = () => createBrowserRouter(routes);
